import { useState, useEffect } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/logowhite.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    if (location.pathname !== "/") {
      navigate("/#" + id);
      setIsOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsOpen(false);
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== "/") {
      navigate("/");
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const productCategories = [
    { title: "Alimentazione Animali Domestici", link: "/prodotti/alimentazione-animali" },
    { title: "Mangimi per Allevamento", link: "/prodotti/mangimi-allevamento" },
    { title: "Prodotti Fitosanitari", link: "/prodotti/fitosanitari" },
    { title: "Concimi e Terricci", link: "/prodotti/concimi-terricci" },
    { title: "Sementi e Piantine", link: "/prodotti/sementi-piantine" },
    { title: "Farine Mulino Fagioli", link: "/prodotti/farine" },
    { title: "Attrezzatura Agricola", link: "/prodotti/attrezzatura-agricola" },
    { title: "Pellet e Combustibili", link: "/prodotti/pellet-combustibili" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-primary shadow-lg" : "bg-primary/95 backdrop-blur-sm"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={handleLogoClick}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img src={logo} alt="Mangimi Rubini" className="h-16 md:h-20 w-auto" />
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("chi-siamo")}
              className="text-white hover:text-cta transition-colors font-medium"
            >
              Chi Siamo
            </button>
            <Link
              to="/calendario-semine"
              className="text-white hover:text-cta transition-colors font-medium"
            >
              Calendario Semine
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="text-white hover:text-cta transition-colors font-medium flex items-center gap-1">
                Prodotti
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64">
                {productCategories.map((category, index) => (
                  <DropdownMenuItem key={index} asChild>
                    <Link to={category.link} className="cursor-pointer">
                      {category.title}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link
              to="/professionisti"
              className="text-white hover:text-cta transition-colors font-medium"
            >
              Professionisti
            </Link>
            <button
              onClick={() => scrollToSection("contatti")}
              className="text-white hover:text-cta transition-colors font-medium"
            >
              Contatti
            </button>
            <a href="tel:+393466257639" className="flex items-center gap-2 text-white hover:text-cta transition-colors">
              <Phone className="w-4 h-4" />
              <span className="font-medium">346 625 7639</span>
            </a>
            <button
              onClick={() => scrollToSection("contatti")}
              className="bg-white text-primary hover:bg-white/90 rounded-full px-6 py-2.5 font-semibold shadow-md hover:shadow-lg transition-all"
            >
              Vieni a trovarci
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white p-2"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-6 animate-fade-in-up">
            <div className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection("chi-siamo")}
                className="text-white hover:text-cta transition-colors font-medium text-left"
              >
                Chi Siamo
              </button>
              <Link
                to="/calendario-semine"
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-cta transition-colors font-medium text-left"
              >
                Calendario Semine
              </Link>
              <div>
                <button
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                  className="text-white hover:text-cta transition-colors font-medium text-left flex items-center gap-1 w-full"
                >
                  Prodotti
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProductsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isProductsOpen && (
                  <div className="mt-2 ml-4 flex flex-col space-y-2">
                    {productCategories.map((category, index) => (
                      <Link
                        key={index}
                        to={category.link}
                        onClick={() => setIsOpen(false)}
                        className="text-white/80 hover:text-cta transition-colors font-medium text-left text-sm"
                      >
                        {category.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link
                to="/professionisti"
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-cta transition-colors font-medium text-left"
              >
                Professionisti
              </Link>
              <button
                onClick={() => scrollToSection("contatti")}
                className="text-white hover:text-cta transition-colors font-medium text-left"
              >
                Contatti
              </button>
              <a href="tel:+393466257639" className="flex items-center gap-2 text-white hover:text-cta transition-colors">
                <Phone className="w-4 h-4" />
                <span className="font-medium">346 625 7639</span>
              </a>
              <button
                onClick={() => scrollToSection("contatti")}
                className="bg-white text-primary hover:bg-white/90 rounded-full font-semibold w-full shadow-md hover:shadow-lg transition-all py-2.5"
              >
                Vieni a trovarci
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
